import React from "react";
import {
  Globe,
  ShoppingBag,
  Layers,
  ArrowUpRight,
  Server,
  TrendingUp,
} from "lucide-react";
import { useReveal } from "../hooks/useReveal";

const services = [
  {
    icon: Globe,
    title: "Strony one-page",
    desc: "Jedna mocna strona, która prowadzi klienta od pierwszego scrolla do telefonu lub formularza.",
    benefit: "Idealne dla usługodawców i firm lokalnych.",
    points: ["Szybka realizacja", "Pełna personalizacja", "Optymalizacja pod SEO"],
  },
  {
    icon: ShoppingBag,
    title: "Sklepy internetowe",
    desc: "WooCommerce z płatnościami, dostawami i panelem, który ogarnie nawet osoba bez wiedzy technicznej.",
    benefit: "Sprzedawaj online od pierwszego tygodnia.",
    points: ["Integracje płatności", "Zarządzanie z panelu", "Mobile-first"],
  },
  {
    icon: Layers,
    title: "Rozbudowane strony",
    desc: "Wielostronicowe serwisy dopasowane do Twojego biznesu — oferta, blog, system rezerwacji, integracje.",
    benefit: "Pełna obecność w sieci, bez kompromisów.",
    points: ["Bloga i SEO", "Wiele podstron", "Indywidualne moduły"],
  },
];

const extras = [
  {
    icon: Server,
    title: "Hosting i opieka",
    desc: "Szybki, niezawodny hosting + dbamy o Twoją stronę. Aktualizujemy treści, ceny w cenniku, jesteśmy gdy coś się dzieje.",
  },
  {
    icon: TrendingUp,
    title: "Pozycjonowanie (SEO)",
    desc: "Optymalizacja techniczna, treści i lokalne SEO — żeby Cię znajdowali sami klienci, bez płatnych reklam.",
  },
];

export const Services = () => {
  const ref = useReveal();
  return (
    <section
      id="uslugi"
      ref={ref}
      data-testid="services-section"
      className="relative py-24 md:py-32 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-stormly-cyan font-bold">
              Co budujemy
            </span>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.05] max-w-2xl">
              Trzy drogi.{" "}
              <span className="text-white/50">Jeden efekt — więcej klientów.</span>
            </h2>
          </div>
          <p className="md:max-w-md text-white/60 text-base">
            Każdy projekt to nie szablon. Robimy stronę, która pasuje do Twojej
            firmy — nie odwrotnie.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className={`reveal reveal-delay-${i + 1} card-glow group relative bg-[#1F1F38] border border-white/10 rounded-3xl p-8 overflow-hidden`}
              >
                <div
                  className="absolute -top-16 -right-16 h-48 w-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(0,255,255,0.15), transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div className="h-12 w-12 rounded-2xl bg-stormly-cyan/10 border border-stormly-cyan/30 flex items-center justify-center mb-6 group-hover:bg-stormly-cyan/20 transition-colors">
                    <Icon size={20} className="text-stormly-cyan" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3">
                    {s.title}
                  </h3>
                  <p className="text-white/65 text-base leading-relaxed mb-6">
                    {s.desc}
                  </p>
                  <p className="text-stormly-cyan text-sm font-semibold mb-6">
                    {s.benefit}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {s.points.map((p) => (
                      <li
                        key={p}
                        className="text-white/70 text-sm flex items-center gap-2"
                      >
                        <span className="h-1 w-1 rounded-full bg-stormly-cyan" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#kontakt"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-stormly-cyan transition-colors"
                  >
                    Zapytaj o wycenę
                    <ArrowUpRight
                      size={16}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Extras: Hosting + SEO */}
        <div className="reveal reveal-delay-3 mt-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs tracking-[0.2em] uppercase text-white/50 font-bold">
              Dodatkowo
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {extras.map((e, i) => {
              const Icon = e.icon;
              return (
                <div
                  key={e.title}
                  data-testid={`extra-service-${i}`}
                  className="card-glow group flex items-start gap-5 bg-[#1F1F38] border border-white/10 rounded-2xl p-6 md:p-7"
                >
                  <div className="h-12 w-12 rounded-xl bg-stormly-cyan/10 border border-stormly-cyan/30 flex items-center justify-center shrink-0 group-hover:bg-stormly-cyan/20 transition-colors">
                    <Icon size={18} className="text-stormly-cyan" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg md:text-xl font-bold mb-2">
                      {e.title}
                    </h3>
                    <p className="text-white/65 text-sm md:text-base leading-relaxed">
                      {e.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
